import classNames from 'classnames';
import { startCase } from 'lodash';
import capitalize from 'lodash/capitalize';
import defaultTo from 'lodash/defaultTo';
import differenceBy from 'lodash/differenceBy';
import get from 'lodash/get';
import isNil from 'lodash/isNil';
import noop from 'lodash/noop';
import toNumber from 'lodash/toNumber';
import moment from 'moment';
import React from 'react';
import Button, { ButtonGroup } from '@atlaskit/button';
import Checkbox from '@atlaskit/checkbox';
import { ErrorMessage, Field, FormFooter } from '@atlaskit/form';
import CopyIcon from '@atlaskit/icon/glyph/copy';
import AddIcon from '@atlaskit/icon/glyph/editor/add';
import LessIcon from '@atlaskit/icon/glyph/editor/divider';
import InfoIcon from '@atlaskit/icon/glyph/info';
import WarningIcon from '@atlaskit/icon/glyph/warning';
import { PanelStateless } from '@atlaskit/panel';
import SectionMessage from '@atlaskit/section-message';
import { ValueType } from '@atlaskit/select';
import Textfield from '@atlaskit/textfield';
import { Y200 } from '@atlaskit/theme/colors';
import Tooltip from '@atlaskit/tooltip';
import {
  DISCRETIONARY_DISCOUNT,
  EntitlementIcon,
  Icon,
  InfoPanel,
  InvoiceGroupFormField,
  InvoiceGroupSelectItem,
  SelectPromotionSelectItem,
  getPromotionDuration,
  isDiscretionaryDiscountPromotion,
  pluraliseBillingCycle,
  renderPromotionValue,
  useFeatureToggle,
} from '@css-xp/base';
import { getLinkedEntitlementsDetails, getProductName } from '@css-xp/base/entitlement-utils';
import { BillingFrequency, PricingPlanCycle, PricingPlanType } from '@atlassian/cssxp-ccp-types';
import {
  EntitlementDetails,
  InvoiceGroup,
  LinkedDestinationEntitlement,
  LinkedProductTypes,
  Maybe,
  Offering,
  PricingPlan,
  Promotion,
} from '@css-xp/graphtypes/src/generated-types-new';
import { getBillEstimate } from '@atlassian/temp-proxies';
import { SkeletonLoader, SkeletonLoaderWrapper } from '@css-xp/skeleton-loader';
import { EntitlementDefaultsResponse, OrderItemType } from '@atlassian/cssxp-types-entitlement';
import { PromotionDuration } from '@atlassian/cssxp-types-promotion';
import { PromotionInstance } from '@atlassian/cssxp-typescommon';
import { MARKETPLACE_PROMO_CODE, getUserTier } from '@atlassian/cssxp-utils-entitlement';
import {
  ENABLE_BILLING_ANCHOR_DATE,
  ENABLE_ORDER_BILL_ESTIMATION,
  ENABLE_PRICING_PLAN_CHANGE,
} from '@atlassian/cssxp-utils-generic';
import { isStatuspage } from '@atlassian/cssxp-utils-product';
import { validateFreeSubscriptions } from '../../../../../entitlement-page/src/components/entitlement-page-utils';
import { ChangePricePlan } from '../../../../components/ChangePricePlan/src/change-price-plan';
import { getCustomPromotionInstance } from '../../../utils/promotion-utils';
import { FormData } from '../manage-entitlement';
import '../manage-entitlement.scss';
import { filterPromotionItem } from '../manage-promotion';
import { formatToUSD } from '../utils/currency-display';
import { POUpdate } from '../utils/manage-po-update';
import { copyReviewChangeContent } from './copy-review-change-content';
import { PriceDifference } from './price-difference';
import { ProductFamilyTable } from './product-family-table';

interface Props {
  entitlement: EntitlementDetails;
  formState: FormData;
  availableInvoiceGroups?: InvoiceGroup[] | never[];
  isSkipTrialChecked: boolean;
  isImmediateFlag: boolean;
  availableOfferings: Offering[];
  availablePricingPlans?: PricingPlan[];
  updateFormState: (data: FormData) => void;
  onDiscretionaryEstimateChange: (obj: DiscretionaryEstimate) => void;
  linkedEntitlementInvoiceGroupId?: Maybe<string> | undefined;
  linkedEntitlementsData?: LinkedDestinationEntitlement[];
  productType?: Maybe<LinkedProductTypes>;
  parentContainerID?: string;
  defaultEntitlementData?: EntitlementDefaultsResponse;
  isBillingCycleChanged?: boolean;
  isUpgradeTrialScenario: boolean;
  isExistingAnnualPlanForJCA?: boolean;
  grandTotal?: number;
  setGrandTotal?: (value: number) => void;
  isCurrentEntitlementFree?: boolean;
  isOfferingChanged?: boolean;
}

type Estimate = {
  response?: any;
  error?: any;
  loading?: boolean;
};

type BillingAnchorResponse = {
  response?: any;
  error?: any;
  loading?: boolean;
};

export interface DiscretionaryEstimate {
  error?: any;
  discretionaryDiscountAmount?: number;
  loading: boolean;
  isDisabled?: boolean;
}

type PromotionInstanceDetail = Promotion & { promotionInstanceId?: string };

const newAddedPromotionValueResolver = (customPromotionId: string, formState: FormData, index: number) => {
  return {
    promotionDefinition: {
      promotionId: customPromotionId,
      customisedValues: !customPromotionId
        ? null
        : {
            applicationReason: {
              id: defaultTo(formState.customReason?.[index]?.value, ''),
            },
            benefits: [
              {
                duration: defaultTo(formState.customDuration?.[index]?.value, ''),
                iterations: defaultTo(toNumber(formState.customIterations?.[index]), undefined),
                value: defaultTo(toNumber(formState.customValue?.[index]), undefined),
              },
            ],
          },
    },
  };
};

const activePromotionsValueResolver = (promotion: PromotionInstanceDetail) => {
  return {
    promotionDefinition: {
      promotionId: promotion.id,
      customisedValues: {
        applicationReason: {
          id: promotion.applicationReason?.id,
        },
        benefits: promotion.benefits,
      },
    },
  };
};

export const ReviewChange = ({
  entitlement,
  formState,
  availableInvoiceGroups,
  isSkipTrialChecked,
  availablePricingPlans,
  updateFormState,
  onDiscretionaryEstimateChange,
  linkedEntitlementInvoiceGroupId,
  linkedEntitlementsData = [],
  productType,
  parentContainerID,
  defaultEntitlementData,
  isBillingCycleChanged,
  isUpgradeTrialScenario,
  isExistingAnnualPlanForJCA,
  grandTotal,
  setGrandTotal,
  isCurrentEntitlementFree = false,
  isOfferingChanged,
}: Props) => {
  const [panelState, setPanelState] = React.useState<boolean>(true);
  const [productsPanelState, setProductsPanelState] = React.useState<boolean>(true);
  const [canUpdatePO, setCanUpdatePO] = React.useState<boolean>(false);
  const [estimatedBill, setEstimatedBill] = React.useState<Partial<Estimate>>({ loading: true });
  const [isEnabled] = useFeatureToggle();
  const isBillEsimationEnabled = isEnabled(ENABLE_ORDER_BILL_ESTIMATION);
  const isChangePricingEnabled = isEnabled(ENABLE_PRICING_PLAN_CHANGE);
  const isBillingAnchorEnabled = isEnabled(ENABLE_BILLING_ANCHOR_DATE);
  const isFreePlan = formState?.newPlan?.label?.includes('Free');
  const isNewBillingFrequencyAnnual = formState?.billingFrequency === BillingFrequency.ANNUAL;

  const discreteDiscountPromotions = React.useMemo(() => {
    const promotions = [];
    const promotionKeys = Object.keys(formState).filter((key) => key.trim().includes('promotionId'));
    for (let i = 0; i < promotionKeys.length; i++) {
      if (isDiscretionaryDiscountPromotion(formState, promotionKeys[i])) {
        promotions.push(getCustomPromotionInstance(i, formState));
      }
    }
    return promotions;
  }, [formState]);

  React.useEffect(() => {
    let anchorTimestamp;
    const dayOfTheMonth = new Date().getDate();
    if (dayOfTheMonth > 28) {
      anchorTimestamp = defaultTo(moment.utc(new Date()).set('date', 28).valueOf(), undefined);
    } else {
      anchorTimestamp = defaultTo(moment.utc(new Date()).valueOf(), undefined);
    }

    updateFormState({
      nextBillingAnchorTimestamp: isNewBillingFrequencyAnnual
        ? anchorTimestamp
        : defaultTo(get(defaultEntitlementData, 'billingAnchorTime.value'), undefined),
    });
  }, [isNewBillingFrequencyAnnual]);

  const anchorTime = formState?.nextBillingAnchorTimestamp;

  React.useEffect(() => {
    const billEstimate = async () => {
      try {
        const promotionIds = [],
          customPromotions: PromotionInstance[] = [];
        const promotionKeys = Object.keys(formState).filter((key) => key.trim().includes('promotionId'));
        for (let i = 0; i < promotionKeys.length; i++) {
          if (isDiscretionaryDiscountPromotion(formState, promotionKeys[i])) {
            const customkey = `customPromotionId-${i}`;
            // @ts-ignore
            const customPromotionId = formState[customkey]?.value;
            customPromotions.push(newAddedPromotionValueResolver(customPromotionId, formState, i));
          } else {
            const promotionObject: any = {
              promotionDefinition: {},
            };
            if (get(formState, promotionKeys[i])?.promotion?.type === MARKETPLACE_PROMO_CODE) {
              promotionObject.promotionDefinition.promotionCode = formState.promoCode;
            } else {
              promotionObject.promotionDefinition.promotionId = get(formState, `${promotionKeys[i]}.value`, '');
            }
            if (promotionObject.promotionDefinition.promotionId || promotionObject.promotionDefinition.promotionCode) {
              promotionIds.push(promotionObject);
            }
          }
        }

        if (!isBillingCycleChanged) {
          defaultTo(
            differenceBy(
              entitlement?.promotions as PromotionInstanceDetail[],
              defaultTo(
                formState?.promotionsToRemove?.map((item) => item?.promotion),
                []
              ),
              'id'
            ),
            []
          ).forEach((promotion: PromotionInstanceDetail) => {
            if (!isNil(promotion)) {
              promotionIds.push({ promotionInstanceId: promotion?.promotionInstanceId });
            }
          });
          if (!isOfferingChanged) {
            differenceBy(
              entitlement?.scheduledChanges?.promotions as PromotionInstanceDetail[],
              defaultTo(entitlement?.promotions as PromotionInstanceDetail[], []),
              'promotionInstanceId'
            ).forEach((promotion: PromotionInstanceDetail) => {
              if (!isNil(promotion)) {
                const removedScheduledPromotionId = formState?.schedulePromotionsToRemove?.find(
                  (scheduledPromotion) => scheduledPromotion?.promotion?.id === promotion?.id
                );
                if (!removedScheduledPromotionId) {
                  if (promotion.isPromotionCustomisable) {
                    promotionIds.push(activePromotionsValueResolver(promotion));
                  } else {
                    promotionIds.push({ promotionDefinition: { promotionId: promotion?.id } });
                  }
                }
              }
            });
          }
        }

        onDiscretionaryEstimateChange({ loading: true });
        const linkedEntitlements =
          isNewBillingFrequencyAnnual && linkedEntitlementsData?.length
            ? getLinkedEntitlementsDetails(
                entitlement,
                linkedEntitlementsData.filter((linkedEntitlementObj) => linkedEntitlementObj.subscriptionId)
              )
            : [];
        const priceEligibility = availablePricingPlans?.find(
          (pricingPlan) => pricingPlan?.id === (formState?.pricePlan?.value || formState?.pricePlanId)
        )?.type;
        const response = await getBillEstimate({
          transactionAccountId: defaultTo(entitlement?.transactionAccountId, ''),
          invoiceGroupId: defaultTo(formState?.invoiceGroupId?.value, ''),
          entitlementId: entitlement.id,
          orderType: OrderItemType.AMENDMENT_ORDER,
          offeringId: formState?.newPlan?.value ?? entitlement?.offeringId ?? undefined,
          units: formState?.units ?? formState?.userTier?.value,
          billingFrequency: (formState?.billingFrequency as unknown) as PricingPlanCycle,
          pricingPlanType: priceEligibility as PricingPlanType,
          planType:
            formState?.pricePlan?.pricingPlanType ?? defaultTo(get(entitlement, 'pricingPlan.pricingPlanType'), null),
          promotionIds,
          customPromotions,
          skipTrial: isStatuspage(entitlement?.productId)
            ? undefined
            : Boolean(isSkipTrialChecked || !!formState?.skipTrial),
          linkedEntitlements,
          isBillingCycleChanged,
        });
        setEstimatedBill({ response, loading: false });
        onDiscretionaryEstimateChange({
          discretionaryDiscountAmount: defaultTo(response?.discretionaryDiscountAmount, 0),
          loading: false,
        });
      } catch (error) {
        onDiscretionaryEstimateChange({ error, loading: false });
        setEstimatedBill({ error, loading: false });
      }
    };

    if (isBillEsimationEnabled) {
      billEstimate();
    }
  }, [formState?.invoiceGroupId?.value]);

  React.useEffect(() => {
    if (!entitlement?.billingGroupId && linkedEntitlementInvoiceGroupId) {
      updateFormState({
        invoiceGroupId: {
          invoiceGroup: {},
          value: linkedEntitlementInvoiceGroupId as string,
        },
      });
    }
  }, [linkedEntitlementInvoiceGroupId]);

  React.useEffect(() => {
    if (!isBillEsimationEnabled && discreteDiscountPromotions.length) {
      onDiscretionaryEstimateChange({ isDisabled: true, loading: false });
    }
  }, [isBillEsimationEnabled, discreteDiscountPromotions]);

  const handleChange = () => {
    setPanelState(!panelState);
  };
  const handleChangeProductDetails = () => {
    setProductsPanelState(!productsPanelState);
  };
  const getFormDataPromotionDuration = (formState: FormData, index: number) => {
    return formState?.customDuration?.[index]?.value === PromotionDuration.REPEATING &&
      formState?.customIterations?.[index]
      ? `${formState.customIterations[index]} ${pluraliseBillingCycle(formState.customIterations[index])}`
      : capitalize(formState?.customDuration?.[index]?.value);
  };

  const getFormDataPromotionReason = (formState: any, index: number) => {
    return startCase(formState?.customReason?.[index]?.label?.toLowerCase()) ?? 'N/A';
  };

  const renderFormDataPromotionValue = (formState: any, index: number) => {
    // FIXME :: We are assuming the discount and % to be only benefit and hard coding it
    const type = startCase(DISCRETIONARY_DISCOUNT.toLowerCase().replace(/_/g, ' '));
    return (
      <>
        <p>Type: {type}</p>
        <p>Benefit: {formState?.customValue?.[index]}% discount</p>
      </>
    );
  };

  const renderPromotionItem = (
    shouldRemove: boolean,
    formState: FormData,
    selectedPromo: SelectPromotionSelectItem,
    index: number
  ) => {
    const promotion = selectedPromo?.promotion;
    return (
      <div>
        <div key={index} className={classNames('promotion-list-item', { 'addition-bg': !shouldRemove })}>
          <strong className={classNames({ 'remove-on': shouldRemove })}>
            {defaultTo(promotion?.title, `Promotion-${index}`)}
          </strong>
          <p className={classNames({ 'remove-on': shouldRemove })}>
            {promotion?.isPromotionCustomisable
              ? renderFormDataPromotionValue(formState, index)
              : renderPromotionValue(promotion, { showDuration: false, showBenefitNextLine: true })}
          </p>
          <p className={classNames({ 'remove-on': shouldRemove })}>{`Duration: ${
            promotion?.isPromotionCustomisable
              ? getFormDataPromotionDuration(formState, index)
              : promotion?.benefits?.[0] && getPromotionDuration(promotion.benefits[0])
          }`}</p>
          {promotion?.isPromotionCustomisable && (
            <p className={classNames({ 'remove-on': shouldRemove })}>{`Reason code: ${getFormDataPromotionReason(
              formState,
              index
            )}`}</p>
          )}
        </div>
      </div>
    );
  };

  const renderBillingAnchorDate = () => {
    if (!isStatuspage(entitlement?.productId) && formState?.billingFrequency === BillingFrequency.ANNUAL) return null;

    const isAnnual = entitlement?.billingFrequency === PricingPlanCycle.ANNUAL;
    return anchorTime ? (
      <div>
        <InfoIcon testId="info-icon" label="Info" size="small" />{' '}
        <label>{`Bill anchor date will be set to ${moment.utc(anchorTime).format('Do')} ${
          isAnnual ? moment.utc(anchorTime).format('MMMM') : ''
        } of every ${isAnnual ? 'year' : 'month'}.`}</label>
      </div>
    ) : null;
  };

  const currentInvoiceGroup = defaultTo(availableInvoiceGroups, []).find(
    (ig) => ig?.id === entitlement?.billingGroupId
  );
  const shipToPartyId = defaultTo(currentInvoiceGroup?.shipToParty?.id, undefined);

  const renderPriceDifference = () => {
    return (
      <PriceDifference
        currentEstimatedBill={entitlement?.nextBillEstimate?.total || 0}
        estimatedBill={estimatedBill}
        setGrandTotal={setGrandTotal}
        entitlement={entitlement}
      />
    );
  };

  return (
    <div className="review-change">
      <h2 onClick={handleChange}>Review changes</h2>
      <span onClick={handleChange} className="header-icon">
        {panelState ? (
          <LessIcon testId="more-icon" label="more-icon" />
        ) : (
          <AddIcon testId="add-icon" label="add-icon" />
        )}
      </span>
      <PanelStateless header={null} isExpanded={panelState} onChange={noop}>
        <div className="current-plans-container">
          <Field name="currentPlan-review" id="currentPlan-review" label="Current plan" isDisabled>
            {() => <p>{formState?.currentPlan}</p>}
          </Field>
          {(isBillingCycleChanged || isExistingAnnualPlanForJCA) && (
            <Field name="currentUser" id="currentUser" label="Current users">
              {() => <p>{isNil(entitlement?.activeUsers) ? '-' : entitlement?.activeUsers}</p>}
            </Field>
          )}
          {isExistingAnnualPlanForJCA && (
            <Field name="currentTier" id="currentTier" label={'Current user tier'}>
              {() => (
                <p>{!isNil(getUserTier(entitlement?.subscription)) ? getUserTier(entitlement?.subscription) : '-'}</p>
              )}
            </Field>
          )}
        </div>
        <Field
          name="billingFrequency"
          label={
            <div>
              Billing cycle
              {isNewBillingFrequencyAnnual && !isFreePlan && (
                <Tooltip content="Free plans aren’t eligible for annual billing" tag={'span'}>
                  <InfoIcon size="small" testId="info-icon" label="Info" />
                </Tooltip>
              )}
            </div>
          }
          defaultValue={formState?.billingFrequency}
          isDisabled
        >
          {() => (
            <div className="buttonGroup-container">
              <ButtonGroup>
                <Button
                  testId="billingFrequency-review-buttonMonthly"
                  id="billFrequency-monthly"
                  appearance={formState?.billingFrequency === BillingFrequency.MONTHLY ? 'primary' : 'default'}
                  className={classNames({ ['selected']: formState?.billingFrequency === BillingFrequency.MONTHLY })}
                  isDisabled
                >
                  Monthly
                </Button>
                <Button
                  testId="billingFrequency-review-buttonAnnual"
                  id="billFrequency-annual"
                  appearance={isNewBillingFrequencyAnnual ? 'primary' : 'default'}
                  className={classNames({ ['selected']: isNewBillingFrequencyAnnual })}
                  isDisabled
                >
                  Annual
                </Button>
              </ButtonGroup>
            </div>
          )}
        </Field>
        <Field label={'New plan'} name="newPlan-review" isDisabled defaultValue={formState?.newPlan?.label}>
          {({ fieldProps }) => <Textfield {...fieldProps} name={'newPlan-review'} testId="newPlan-review-field" />}
        </Field>
        {!isStatuspage(entitlement?.productId) && (
          <Field label="Skip trial" name="skipTrial" isDisabled>
            {({ fieldProps }) => (
              <Checkbox
                {...fieldProps}
                defaultChecked={isSkipTrialChecked || !!formState?.skipTrial}
                testId="skipTrial"
              />
            )}
          </Field>
        )}
        {isUpgradeTrialScenario && (
          <span className="upgrade-trial-banner">
            <WarningIcon label="" primaryColor={Y200} />
            <p>Current plan will be charged during the no-cost trial period of Premium plan</p>
          </span>
        )}
        {isNewBillingFrequencyAnnual && !isFreePlan && !isStatuspage(entitlement.productId) && (
          <Field label="User tier" name="userTier" id="userTier" isDisabled defaultValue={formState?.userTier?.label}>
            {({ fieldProps }) => <Textfield {...fieldProps} name={'userTier'} testId="userTier-field" />}
          </Field>
        )}
        {isStatuspage(entitlement?.productId) && (
          <Field label={'Units'} name="units-review" isDisabled defaultValue={formState?.units}>
            {({ fieldProps }) => <Textfield {...fieldProps} name={'units-review'} testId="units-review-field" />}
          </Field>
        )}

        {isChangePricingEnabled ? (
          <ChangePricePlan
            entitlement={entitlement}
            isManagePane={false}
            formState={formState}
            availablePricingPlans={availablePricingPlans}
            planId={formState?.pricePlan?.value ?? entitlement?.pricingPlan?.id}
          />
        ) : (
          <Field name="pricing-review" id="pricing-review" label="Pricing" isDisabled>
            {() => (
              <div className="pricing-inline">
                <span>{capitalize(formState?.pricing)}</span>
              </div>
            )}
          </Field>
        )}

        {defaultTo(formState?.promotionsToRemove?.length, 0) > 0 && (
          <Field label={'Remove promotion'} name="removePromotions-review" isDisabled>
            {() => defaultTo(formState?.promotionsToRemove, []).map(renderPromotionItem.bind(null, true, formState))}
          </Field>
        )}
        {defaultTo(filterPromotionItem(formState), []).length > 0 && (
          <Field label={'Add discount'} name="addPromotions-review" isDisabled>
            {() => (
              <>
                {isNewBillingFrequencyAnnual && !isStatuspage(entitlement.productId) && (
                  <div className="current-ent-details">
                    The below discount(s) will be applied to
                    <div>
                      <EntitlementIcon
                        logoId={entitlement?.logoId as string}
                        productId={entitlement?.productId as string}
                        isCloud={true}
                        className="avatar medium"
                      />{' '}
                      <span>{`${getProductName(entitlement)} ${entitlement?.entitlementNumber}`}</span>
                    </div>
                  </div>
                )}
                {defaultTo(filterPromotionItem(formState), []).map(renderPromotionItem.bind(null, false, formState))}
              </>
            )}
          </Field>
        )}
      </PanelStateless>

      {isNewBillingFrequencyAnnual && (
        <>
          <h2 onClick={handleChangeProductDetails}>Subscription details</h2>
          <span onClick={handleChangeProductDetails} className="product-family-icon">
            {productsPanelState ? (
              <LessIcon testId="more-icon" label="more-icon" />
            ) : (
              <AddIcon testId="add-icon" label="add-icon" />
            )}
          </span>
          <PanelStateless header={null} isExpanded={productsPanelState} onChange={noop}>
            <ProductFamilyTable
              entitlement={entitlement}
              linkedEntitlementsData={linkedEntitlementsData}
              parentContainerID={parentContainerID}
              productType={productType}
              billingCycle={(formState?.billingFrequency as unknown) as PricingPlanCycle}
              pricingPlan={formState?.newPlan?.label ?? formState.currentPlan}
              userTier={isStatuspage(entitlement.productId) ? formState?.units : formState?.userTier?.value}
              estimatedBill={estimatedBill}
              anchorTime={anchorTime}
            />
          </PanelStateless>
        </>
      )}
      {isBillEsimationEnabled && renderPriceDifference()}
      {isNewBillingFrequencyAnnual && (
        <div className="consent-info-container">
          <InfoPanel
            type="warning"
            title="Customer consent is required"
            message={
              <p>
                Changes made to this subscription will require customer consent to be recorded. Use the button below to
                copy the details to your customer’s GSAC ticket.
                <br />
                <br />
                If you’ve already received consent, enter the GSAC ticket details in the Support ticket link field
                below.
              </p>
            }
          />
          <Button
            iconBefore={<CopyIcon label="" size="medium" />}
            onClick={() =>
              copyReviewChangeContent({
                selectedEntitlement: {
                  entitlementNumber: entitlement?.entitlementNumber,
                  productName: entitlement?.productName,
                  billingFrequency: formState?.billingFrequency,
                  pricingPlan: formState?.newPlan?.label || formState?.currentPlan,
                  userTier: !isFreePlan && !isStatuspage(entitlement.productId) ? formState?.userTier?.label : null,
                  units: isStatuspage(entitlement.productId) ? formState?.units : null,
                  parentContainerID,
                  isCurrentEntitlementFree,
                },
                linkedEntitlements: !isStatuspage(entitlement.productId) ? linkedEntitlementsData : [],
                linkedEntitlementsBillEstimate: estimatedBill?.response?.linkedEstimates,
                currentEntitlementBillEstimate: estimatedBill?.response?.estimate,
                billDate: anchorTime
                  ? `${moment.utc(anchorTime).format('Do')} ${moment.utc(anchorTime).format('MMMM')}`
                  : null,
                grandTotal,
              })
            }
            appearance="primary"
          >
            Copy details for approval
          </Button>
        </div>
      )}

      {isUpgradeTrialScenario && (
        <SectionMessage appearance="information" testId="upgrade-trial-info-banner">
          <p>
            The customer will be moved to a no-cost trial for the Premium plan but will be charged for their current
            plan.
          </p>
        </SectionMessage>
      )}
      <h2>Submit changes</h2>
      {isBillingAnchorEnabled && renderBillingAnchorDate()}
      <InvoiceGroupFormField
        isRequired
        isDisabled={!validateFreeSubscriptions(entitlement) || availableInvoiceGroups?.length === 1}
        availableInvoiceGroups={availableInvoiceGroups}
        entitlementBillingInvoiceGroupId={formState?.invoiceGroupId?.value}
        fieldLabel="Billing profile"
        onChange={(value: ValueType<InvoiceGroupSelectItem, false> | React.FormEvent<HTMLSelectElement>) => {
          updateFormState({
            invoiceGroupId: value as InvoiceGroupSelectItem,
          });
        }}
      />
      {!canUpdatePO && (
        <Button
          testId="add-po-button"
          onClick={() => setCanUpdatePO(true)}
          iconBefore={<AddIcon label="add" />}
          appearance="link"
        >
          Update PO number
        </Button>
      )}
      {canUpdatePO && (
        <POUpdate
          setCanUpdatePO={setCanUpdatePO}
          billingGroupId={formState?.invoiceGroupId?.value}
          entitlement={entitlement}
          shipToPartyId={shipToPartyId}
        />
      )}
      <Field
        label={'Support ticket number'}
        name="supportTicketLink"
        isRequired
        validate={(value: any) => (value && value.length > 0 ? undefined : 'INVALID')}
      >
        {({ fieldProps, error }) => (
          <>
            <Textfield
              className="support-ticket"
              {...fieldProps}
              name={'supportTicketLink'}
              testId="supportTicketLink-field"
              placeholder={'Add GSAC ticket number'}
            />
            {error && <ErrorMessage testId="supportTicketLink-error">Enter a valid ticket number</ErrorMessage>}
          </>
        )}
      </Field>
      {isNewBillingFrequencyAnnual && (
        <div className="customer-consent-field">
          <Field
            name="consentTaken"
            isRequired
            validate={(value: any) => (!value ? 'Please confirm customer consent' : undefined)}
          >
            {({ fieldProps, error }) => (
              <>
                <Checkbox
                  {...fieldProps}
                  defaultChecked={!!formState?.consentTaken}
                  label="I confirm customer consent has been received"
                  testId="consent-taken-checkbox"
                />
                {error && <ErrorMessage testId="customer-consent-error">{error}</ErrorMessage>}
              </>
            )}
          </Field>
        </div>
      )}
      <Field
        label={'Internal comment'}
        name="notes"
        isRequired
        validate={(value: any) => (value && value.length > 0 ? undefined : 'INVALID')}
      >
        {({ fieldProps, error }) => (
          <>
            <Textfield
              className="internal-notes"
              {...fieldProps}
              name={'notes'}
              testId="notes-field"
              placeholder={'Add order comments'}
            />
            {error && <ErrorMessage testId="supportTicketLink-error">This field is required</ErrorMessage>}
          </>
        )}
      </Field>
      {discreteDiscountPromotions.length && !isBillEsimationEnabled ? (
        <div className="approval-info-message" data-testid="bill-estimate-disabled-custom-warning">
          <Icon type="warning" />
          <span>
            We can’t show the price estimate right now. Need help? Contact <strong>#help-advocate-central</strong>{' '}
            channel.
          </span>
        </div>
      ) : null}
    </div>
  );
};
